import { useState, useCallback, useEffect, useRef } from 'react'
import Cropper from 'react-easy-crop'

const createImage = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image()
        img.addEventListener('load', () => resolve(img))
        img.addEventListener('error', reject)
        img.src = url
    })

const getCroppedBlob = async (imageSrc, pixelCrop, outputSize = 600) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    canvas.width = outputSize
    canvas.height = outputSize
    const ctx = canvas.getContext('2d')
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, outputSize, outputSize
    )
    return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9))
}

const CropPreviewCanvas = ({ rawSrc, croppedAreaPixels }) => {
    const canvasRef = useRef(null)

    useEffect(() => {
        if (!rawSrc || !croppedAreaPixels || !canvasRef.current) return
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        const img = new Image()
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(
                img,
                croppedAreaPixels.x, croppedAreaPixels.y,
                croppedAreaPixels.width, croppedAreaPixels.height,
                0, 0, canvas.width, canvas.height
            )
        }
        img.src = rawSrc
    }, [rawSrc, croppedAreaPixels])

    return (
        <canvas
            ref={canvasRef}
            width={200}
            height={200}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
        />
    )
}

const ImageCropUploader = ({ onUpload, currentImageUrl, uploadUrl = `${process.env.REACT_APP_API_URL}/api/upload/coupon-image` }) => {
    const [rawSrc, setRawSrc] = useState(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(currentImageUrl || null)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState('')
    const fileInputRef = useRef(null)

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels)
    }, [])

    const handleFileChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            setRawSrc(reader.result)
            setCrop({ x: 0, y: 0 })
            setZoom(1)
            setCroppedAreaPixels(null)
        }
        reader.readAsDataURL(file)
        e.target.value = ''
    }

    const handleApply = async () => {
        if (!croppedAreaPixels || !rawSrc) return
        setUploading(true)
        setError('')
        try {
            const blob = await getCroppedBlob(rawSrc, croppedAreaPixels)
            const localUrl = URL.createObjectURL(blob)
            setPreviewUrl(localUrl)

            const formData = new FormData()
            formData.append('file', blob, 'coupon-image.jpg')
            const res = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Upload failed')

            onUpload(data.url)
            setRawSrc(null)
        } catch (err) {
            setError(err.message || 'Upload failed. Please try again.')
        } finally {
            setUploading(false)
        }
    }

    return (
        <div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {!rawSrc ? (
                previewUrl ? (
                    <div className="d-flex align-items-center gap-16">
                        <div style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', flexShrink: 0 }}>
                            <img src={previewUrl} alt="Coupon" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div>
                            <p className="text-success-600 fw-medium text-sm mb-8 d-flex align-items-center gap-6">
                                <i className="ph-fill ph-check-circle" /> Photo uploaded
                            </p>
                            <button
                                type="button"
                                className="btn btn-outline-main rounded-8 px-16 py-8 text-sm"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Change Photo
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border rounded-16 p-32 text-center"
                        style={{ cursor: 'pointer', borderStyle: 'dashed', borderColor: '#d1d5db' }}
                    >
                        <span className="d-flex justify-content-center mb-12 text-gray-400" style={{ fontSize: 48 }}>
                            <i className="ph ph-image" />
                        </span>
                        <p className="fw-medium text-gray-600 mb-4">Click to upload coupon photo</p>
                        <p className="text-gray-400 text-sm mb-0">JPG, PNG or WEBP</p>
                    </div>
                )
            ) : (
                <div>
                    <div className="row g-16">
                        {/* Crop editor */}
                        <div className="col-lg-7">
                            <p className="fw-medium text-gray-700 mb-8 text-sm">Crop & Resize</p>
                            <div style={{ position: 'relative', width: '100%', height: 320, background: '#111827', borderRadius: 12, overflow: 'hidden' }}>
                                <Cropper
                                    image={rawSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-12 mt-12">
                                <i className="ph ph-magnifying-glass-minus text-gray-500" style={{ fontSize: 18 }} />
                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.05}
                                    value={zoom}
                                    onChange={e => setZoom(Number(e.target.value))}
                                    className="form-range flex-grow-1"
                                />
                                <i className="ph ph-magnifying-glass-plus text-gray-500" style={{ fontSize: 18 }} />
                            </div>
                        </div>

                        {/* Card preview */}
                        <div className="col-lg-5">
                            <p className="fw-medium text-gray-700 mb-8 text-sm">Card Preview</p>
                            <div className="product-card p-16 border border-gray-100 rounded-16" style={{ maxWidth: 220 }}>
                                <div
                                    className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative w-100"
                                    style={{ overflow: 'hidden', height: 160 }}
                                >
                                    {croppedAreaPixels ? (
                                        <CropPreviewCanvas rawSrc={rawSrc} croppedAreaPixels={croppedAreaPixels} />
                                    ) : (
                                        <span className="text-gray-300" style={{ fontSize: 32 }}>
                                            <i className="ph ph-image" />
                                        </span>
                                    )}
                                </div>
                                <div className="mt-12">
                                    <span className="text-main-600 bg-main-50 text-xs fw-medium py-2 px-8">—% OFF</span>
                                    <p className="fw-semibold text-sm mt-8 mb-4 text-line-2">Coupon title will appear here</p>
                                    <p className="text-gray-400 text-xs mb-0 text-line-3">Description preview...</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && <div className="alert alert-danger mt-12 text-sm">{error}</div>}

                    <div className="d-flex gap-12 mt-16 flex-wrap">
                        <button
                            type="button"
                            className="btn bg-main-600 text-white rounded-8 px-24 py-11 fw-medium"
                            onClick={handleApply}
                            disabled={uploading || !croppedAreaPixels}
                        >
                            {uploading ? 'Uploading...' : 'Apply & Upload'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-main rounded-8 px-24 py-11"
                            onClick={() => setRawSrc(null)}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ImageCropUploader
